
import { Color, createColor, rgb2hex, saturationToPoint, validateHex } from '../../../lib/Graphics/Color';
import * as Dim2 from '../../../lib/Graphics/Dim2';
import './ColorPicker.css';
import { forwardRef, ReactNode, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Menu } from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { clamp } from '../../../lib/Math';

interface ColorPickerProperties
{
    ariaLabel?: string
    title?: string
    icon: IconDefinition
    color: string
    children?: ReactNode
    onSelect?: (color: Color) => void
    onChange?: (color: Color) => void
}

const basicColors = ['#d0021b', '#f5a623', '#f8e71c', '#8b572a', '#7ed321', '#417505', '#bd10e0', '#9013fe', '#4a90e2', '#50e3c2', '#b8e986','#000000', '#4a4a4a', '#9b9b9b', '#ffffff'];

const defaultColor = rgb2hex({ r: 20, g: 20, b: 20 });

interface MoveWrapperProperties
{
    className?: string;
    style?: React.CSSProperties;
    onChange: (position: Dim2.Point, size: Dim2.Size) => void;
    children: JSX.Element;
}

const MoveWrapper = forwardRef<HTMLDivElement, MoveWrapperProperties>(({ className, style, onChange, children }, ref) =>
{
    const divRef = useRef<HTMLDivElement>(null);
    useImperativeHandle(ref, () => divRef.current as HTMLDivElement, []);
    const move = (e: React.MouseEvent | MouseEvent): void =>
    {
        if (divRef.current)
        {
            const { current: div } = divRef;
            const { width, height, left, top } = div.getBoundingClientRect();
            const x = clamp(e.clientX - left, 0, width);
            const y = clamp(e.clientY - top, 0, height);
            onChange({ x, y }, { width, height });
        }
    };
    const onMouseDown = (e: React.MouseEvent): void =>
    {
        if (e.button !== 0) { return; }
        move(e);
        const onMouseMove = (e: MouseEvent) =>
        {
            move(e);
        };
        const onMouseUp = (e: MouseEvent) =>
        {
            document.removeEventListener('mousemove', onMouseMove, false);
            document.removeEventListener('mouseup', onMouseUp, false);
            move(e);
        };
        document.addEventListener('mousemove', onMouseMove, false);
        document.addEventListener('mouseup', onMouseUp, false);
    };
    const element =
    <div
        ref={divRef}
        className={className}
        style={style}
        onMouseDown={onMouseDown}>
        {children}
    </div>;
    return element;
});

MoveWrapper.displayName = 'MoveWrapper';

export default function ColorPicker({ color, children, onSelect, onChange, icon, ...rest }: Readonly<ColorPickerProperties>): JSX.Element
{
    const [value, setValue2] = useState(createColor('hex', validateHex(color, defaultColor)));
    const [inputColor, setInputColor] = useState(color);
    const saturationRef = useRef<HTMLDivElement>(null);
    const hueRef = useRef<HTMLDivElement>(null);
    const [saturationPosition, setSaturationPosition] = useState({ x: 0, y: 0 });
    const [huePosition, setHuePosition] = useState(0);

    const setValue = (value: Color) =>
    {
        if (value.hsv.s > 1)
        {
            // eslint-disable-next-line no-debugger
            debugger;
        }
        setValue2(value);
    };

    const onSetHex = (hex: string) =>
    {
        setInputColor(hex);
        if (/^#[0-9A-Fa-f]{6}$/i.test(hex))
        {
            const newColor = createColor('hex', hex);
            setValue(newColor);
        }
    };

    const onMoveSaturation = ({ x, y }: Dim2.Point, { width, height }: Dim2.Size) =>
    {
        const newHsv = { ...value.hsv, s: (x / width), v: 1 - (y / height) };
        const newColor = createColor('hsv', newHsv);
        setValue(newColor);
        setInputColor(newColor.hex);
    };

    const onMoveHue = ({ x }: Dim2.Point, { width }: Dim2.Size) =>
    {
        const newHsv = {...value.hsv, h: (x / width) * 360};
        const newColor = createColor('hsv', newHsv);
        setValue(newColor);
        setInputColor(newColor.hex);
    };

    useEffect(() =>
    {
        if (saturationRef.current)
        {
            const point = saturationToPoint(value, saturationRef.current.clientWidth, saturationRef.current.clientHeight);
            setSaturationPosition(point);
        }
    },
    [value, saturationRef]);

    useEffect(() =>
    {
        if (hueRef.current)
        {
            setHuePosition((value.hsv.h / 360) * hueRef.current.clientWidth);
        }
    },
    [value, hueRef]);

    useEffect(() =>
    {
        // Check if the dropdown is actually active
        if (onChange)
        {
            onChange(value);
            setInputColor(value.hex);
        }
    },
    [value, onChange]);

    useEffect(() =>
    {
        if (!color) { return; }
        const newColor = createColor('hex', color);
        setValue(newColor);
        setInputColor(newColor.hex);
    },
    [color]);

    const handleOnSelect = useCallback(() =>
    {
        onSelect?.(value);
    },
    [value]);

    const element =
    <Menu as="div" className="menu color-picker">
        <div className="btn-group">
            <div className="btn" onClick={handleOnSelect}>
                <div>
                    <FontAwesomeIcon icon={icon} />
                    <div style={{ minHeight: '3px', backgroundColor: value?.hex }}></div>
                </div>
            </div>
            <Menu.Button className="btn">
                <FontAwesomeIcon icon={faCaretDown} className="caret" />
            </Menu.Button>
        </div>
        <Menu.Items className="menu-content">
            <input type="text" className='form-control' onChange={e => onSetHex(e.target.value)} value={inputColor} />
            <div className="color-picker-basic-color">
                {basicColors.map((colorValue) => (
                    <button type='button'
                        className={colorValue === value.hex ? ' active' : ''}
                        key={colorValue}
                        style={{ backgroundColor: colorValue }}
                        onClick={() => { setInputColor(colorValue); setValue(createColor('hex', colorValue)); }}
                    />
                ))}
            </div>
            <MoveWrapper ref={saturationRef} className="color-picker-saturation" style={{ backgroundColor: `hsl(${value.hsv.h}, 100%, 50%)` }} onChange={onMoveSaturation}>
                <div className="color-picker-saturation_cursor" style={{ backgroundColor: value.hex, left: saturationPosition.x, top: saturationPosition.y }} />
            </MoveWrapper>
            <MoveWrapper ref={hueRef} className="color-picker-hue" onChange={onMoveHue}>
                <div className="color-picker-hue_cursor" style={{ backgroundColor: `hsl(${value.hsv.h}, 100%, 50%)`, left: huePosition }} />
            </MoveWrapper>
            <div className="color-picker-color" style={{ backgroundColor: value.hex }} />
        </Menu.Items>
    </Menu>;
    return element;
}
